import Animated, {
  useSharedValue,
  useAnimatedStyle,
  useAnimatedProps,
  useDerivedValue,
  SlideInRight,
  SlideOutDown,
  Layout,
  measure,
  interpolate,
  withTiming,
  withDelay,
  withSequence,
  Easing,
  runOnJS,
} from 'react-native-reanimated';
import React, { Component, useState, useEffect } from 'react';
import { Platform, PanResponder } from 'react-native';
import PropTypes from 'prop-types';
import Svg, { Defs, Pattern, G, Rect, Circle, Path, RadialGradient, Stop } from 'react-native-svg';
import HexUtils from './HexUtils';
import Hex from './models/Hex';
import Point from './models/Point';
import Orientation from './models/Orientation';
import Board from '../models/Board';
const AnimatedSvg = Animated.createAnimatedComponent(Svg);
const AnimatedCircle = Animated.createAnimatedComponent(Circle);
import utils from '../utils/utils.js';
import theme from '../styles/theme';
import { playSound } from '../utils/soundUtils.js';
import { withBoardContext } from '../contexts/BoardContext';

// Drag Vars
let startX = 0;
let startY = 0;
let startTranslateX = 0;
let startTranslateY = 0;
let diffX = 0;
let diffY = 0;
let isDoubleTap = false;
let hasSelectedTile = false;
let dragTimeout = null;
let dragging = false;
let isTwoFingerDrag = false;
let moveIndex = 0;
let hasClicked = false;
let hasLeftOriginHex = false;
let moving = false;
let moveThreshold = 0;
let lastHoveredHex = null;

const HexGrid = (props) => {
  const { width, height, layout, viewBox = "-50 -50 100 100", triggerTransition, isGame, setShowTransition, lastTileHex } = props
  const { setIsPointerDown, selectTile, findMaxUsedRadius } = props.board;

  useEffect(() => {
    if (Platform.OS === 'web') {
      if (triggerTransition === false) setShowTransition(false);
      if (triggerTransition) setShowTransition(true);

    } else {

      // // Zoom in automatically on small puzzles
      // if (triggerTransition === false) {
      //   setShowTransition(false);
      //   let maxR = findMaxUsedRadius();
      //   if (maxR <= 4) {
      //     setTimeout(() => {
      //       scale.value = withTiming(maxScale, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) });
      //       translateX.value = withTiming(-(width * maxScale - width) / 2, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) });
      //       translateY.value = withTiming(-(height * maxScale - height) / 2, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) });
      //     }, 1000)
      //   }
      // }

      // Setup transition animation
      if (triggerTransition) {
        if (scale.value === maxScale) {
          scale.value = withTiming(1, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) }, () => {
            runOnJS(setShowTransition)(true);
          });
          translateX.value = withTiming(0, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) });
          translateY.value = withTiming(0, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) });
        } else {
          setShowTransition(true);
        }
      }

    }
  }, [triggerTransition]);

  const onPointerUp = () => {
    setIsPointerDown(false);
  }

  let touchEvents = {
    onPointerUp: onPointerUp,
    onPointerLeave: onPointerUp
  }
  if (Platform.OS !== 'web' ||
      (utils.isTouchDevice() && isGame)) {
    touchEvents = {};
  }

  const maxScale = 1.5;
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const scale = useSharedValue(1);
  const isZoomed = scale.value === maxScale;


  const getHexFromEvt = (evt) => {
    let adjustedX = evt.nativeEvent.locationX - (width / 2);
    let adjustedY = evt.nativeEvent.locationY - (height / 2);
    if (isZoomed) {
      let centerX = -(width * maxScale - width) / 2;
      let centerY = -(height * maxScale - height) / 2;
      adjustedX += centerX;
      adjustedY += centerY;
      adjustedX /= maxScale;
      adjustedY /= maxScale;
    }
    let offsetPixel = new Point(adjustedX, adjustedY);
    let hoveredHex = HexUtils.pixelToPointyHex(offsetPixel, layout, width);
    return hoveredHex;
  }

  const playNote = (i) => {
    if (i === 1) playSound('note2');
    if (i === 2) playSound('note1');
    if (i === 3) playSound('note2');
    if (i === 4) playSound('note3');
    if (i === 5) playSound('note4');
    if (i === 6) playSound('note5');
    if (i === 7) playSound('note4');
    if (i === 8) playSound('note5');
    if (i === 9) playSound('note6');
    if (i === 10) playSound('note7');
    if (i === 11) playSound('note8');
    if (i === 12) playSound('note6');
    if (i === 13) playSound('note7');
    if (i >= 14) {
      let odd = i % 2 === 0;
      if (odd) {
        playSound('note8');
      } else {
        playSound('note8alt');
      }
    }
  }

  const onClick = (pos) => {
    const { chooseStartTileMode, setChooseStartTileMode, setTile, setWord } = props.board;
    let hex = utils.getPropertyByKeys(props.board, [pos]);
    if (!hex) return;

    if (isGame) {
      selectTile(hex);
      let letterIndex = (utils.getPropertyByKeys(props.board, [`word${hex.wordIndex}selected`]) || []).length;
      playNote(letterIndex);

    } else if (chooseStartTileMode) {
      let { index, word, letterIndex, lastHex } = chooseStartTileMode;
      let wordObj = utils.getPropertyByKeys(props.board, [`word${index}`]);
      let newHex = hex;
      if (!newHex.wordIndex &&
          (!lastHex ||
           HexUtils.distance(newHex, lastHex) === 1)) {
        playNote(letterIndex + 2);
        newHex.letter = word[letterIndex];
        newHex.wordIndex = index;
        setTile(newHex);
        if (letterIndex === 0) {
          setWord(newHex, wordObj.word, wordObj.color, index);
        }
        if (letterIndex === word.length - 1) {
          setChooseStartTileMode(false);
        } else {
          setChooseStartTileMode(index, letterIndex + 1, hex);
        }
      }

    }
  }

  const resetDrag = () => {
    const { setIsPointerDown } = props.board;

    // Reset dragging vars
    setIsPointerDown(false);
    hasSelectedTile = false;
    dragTimeout = null;
    dragging = false;
    isTwoFingerDrag = false;
    moveIndex = 0;
    hasClicked = false;
    hasLeftOriginHex = false;
    moving = false;
    moveThreshold = 0;
    lastHoveredHex = null;
    startX = 0;
    startY = 0;
  }

  const tileIsAdjacent = (hex, otherTile) => {
    return (HexUtils.distance(hex, otherTile) === 1);
  }

    // Setup pan responder
  const svgPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: (evt, gestureState) => true,
    onStartShouldSetPanResponderCapture: (evt, gestureState) => true,
    onMoveShouldSetPanResponder: (evt, gestureState) => true,
    onMoveShouldSetPanResponderCapture: (evt, gestureState) => true,
    onPanResponderGrant: (evt, gs) => {
      diffX = 0;
      diffY = 0;
      startTranslateX = translateX.value;
      startTranslateY = translateY.value;


      // Select tile immediately
      if (!isZoomed) {
        let hoveredHex = getHexFromEvt(evt);
        let hoveredPosition = Board.positionToString(hoveredHex);
        hasClicked = true;
        onClick(hoveredPosition);
      }
    },
    onPanResponderMove: (evt, gs) => {
      if (!startX) startX = gs.moveX;
      if (!startY) startY = gs.moveY;
      diffX = gs.moveX - startX;
      diffY = gs.moveY - startY;
      let newTranslateX = startTranslateX + diffX;
      let newTranslateY = startTranslateY + diffY;
      let maxY = (scale.value * height) - height;
      let maxX = (scale.value * width) - width;
      if (newTranslateX >= maxX) newTranslateX = maxX;
      if (newTranslateX <= -maxX) newTranslateX = -maxX;
      if (newTranslateY >= maxY) newTranslateY = maxY;
      if (newTranslateY <= -maxY) newTranslateY = -maxY;

      let hoveredHex = getHexFromEvt(evt);
      let hoveredPosition = Board.positionToString(hoveredHex);
      let currHex = props.board[hoveredPosition];

      let threshold = 2;

      // First move calculation to test if 2 finger drag
      if (moveIndex < threshold &&
          isZoomed) {
        if (!isTwoFingerDrag && gs.numberActiveTouches >= 2) {
          isTwoFingerDrag = true;
        }
      }
      if (moveIndex === threshold) {
        if (!isTwoFingerDrag && !hasClicked) {
          hasClicked = true;
          onClick(hoveredPosition);
        }
      }

      if (isTwoFingerDrag) {
        // Make sure still 2fd
        if (gs.numberActiveTouches >= 2) {
          translateX.value = newTranslateX;
          translateY.value = newTranslateY;
        }

      // Wait for 2nd move event to check for 2 finger drag
      } else if (moveIndex >= threshold) {

        // Handle touch word selection
        if (props.board.hasOwnProperty(hoveredPosition) &&
            (!lastHoveredHex ||
             (!HexUtils.equals(lastHoveredHex, hoveredHex) &&
              tileIsAdjacent(lastHoveredHex, hoveredHex)))) {
          if (moveThreshold >= 3) {

            if (!moving) {
              moving = true;
              setIsPointerDown(true);
            }

            if (!currHex.wordIndex) {
              selectTile(currHex);
              let letterIndex = (utils.getPropertyByKeys(props.board, [`word${currHex.wordIndex}selected`]) || []).length;
              playNote(letterIndex);
            }

            lastHoveredHex = hoveredHex;
            hasSelectedTile = true;
            moveThreshold = 0;
          } else {
            moveThreshold++;
          }
        }
      }

      moveIndex++;
    },
    onPanResponderTerminationRequest: (evt, gestureState) => true,
    onPanResponderRelease: (evt, gs) => {
      // Capture clicks when zoomed in but doesn't trigger on move handler
      if (!isTwoFingerDrag && !hasClicked && isZoomed) {
        let hoveredHex = getHexFromEvt(evt);
        let hoveredPosition = Board.positionToString(hoveredHex);
        hasClicked = true;
        onClick(hoveredPosition);
      }

      // Don't zoom on editor or larger screens
      // if (isDoubleTap === true &&
      //     isGame &&
      //     theme.isSmallScreen) {
      //   let newScale = scale.value === 1 ? maxScale : 1;
      //   if (scale.value === maxScale) {
      //     scale.value = withTiming(1, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) });
      //     translateX.value = withTiming(0, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) });
      //     translateY.value = withTiming(0, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) });
      //   } else {
      //     scale.value = withTiming(maxScale, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) });
      //     translateX.value = withTiming(-(width * maxScale - width) / 2, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) });
      //     translateY.value = withTiming(-(height * maxScale - height) / 2, { duration: 400, easing: Easing.bezier(0.65, 0, 0.35, 1) });
      //   }
      // } else {
      //   translateX.value = 2 * Math.round(translateX.value / 2);
      //   translateY.value = 2 * Math.round(translateY.value / 2);
      // }

      startTranslateX = translateX.value;
      startTranslateY = translateY.value;


      isDoubleTap = true;
      setTimeout(() => {
        isDoubleTap = false;
      }, 200);

      // Reset drag vars and state
      resetDrag();
    },
    onShouldBlockNativeResponder: (evt, gestureState) => {
      // Returns whether this component should block native components from becoming
      // the JS responder. Returns true by default. Is currently only supported on
      // android.
      return true;
    }
  })

  let textureColor = 'rgba(0,0,0,.2)';
  const scaledWidth = width * scale.value;
  const scaledHeight = height * scale.value;
  let svgProps = {};

  if (Platform.OS !== 'web') {
    svgProps = {...svgPanResponder.panHandlers};
    let animatedProps = useAnimatedProps(() => {
      return {
        width: width * scale.value,
        height: height * scale.value,
      };
    });
    svgProps.animatedProps = animatedProps;
  } else {
    svgProps.width = width;
    svgProps.height = height;
  }

  let circleProps = useAnimatedProps(() => {
    return {
      cx: translateX.value,
      cy: translateY.value,
    };
  });

  const translationStyle = useAnimatedStyle(() => {
    return {
      width: width,
      height: height,
      transform: [{ translateX: translateX.value }, { translateY: translateY.value }]
    };
  });

  // android
  let defs = (
    <Defs>
      <Pattern id='texture-scale' patternUnits='userSpaceOnUse' width='20' height='20' patternTransform='scale(.2) rotate(0)'>
        <Path d='M-10-10A10 10 0 00-20 0a10 10 0 0010 10A10 10 0 010 0a10 10 0 00-10-10zM10-10A10 10 0 000 0a10 10 0 0110 10A10 10 0 0120 0a10 10 0 00-10-10zM30-10A10 10 0 0020 0a10 10 0 0110 10A10 10 0 0140 0a10 10 0 00-10-10zM-10 10a10 10 0 00-10 10 10 10 0 0010 10A10 10 0 010 20a10 10 0 00-10-10zM10 10A10 10 0 000 20a10 10 0 0110 10 10 10 0 0110-10 10 10 0 00-10-10zM30 10a10 10 0 00-10 10 10 10 0 0110 10 10 10 0 0110-10 10 10 0 00-10-10z'  strokeWidth='1.5' stroke={textureColor} fill='none'/>
      </Pattern>
      <Pattern id='texture-chevron' patternUnits='userSpaceOnUse' width='20' height='20' patternTransform='scale(.12) rotate(0)'>
        <Path d='M-5 5L5.1 15 15 5l10 10'  strokeLinecap='square' strokeWidth='2' stroke={textureColor} fill='none'/>
      </Pattern>
      <Pattern id='texture-geometric' patternUnits='userSpaceOnUse' width='100' height='100' patternTransform='scale(.2) rotate(0)'>
        <Path d='M0 0v1l1-1H0zm6.29 0-.147.143 11.81 11.619 8.19-8.143L22.666.047 22.709 0h-1.998l-.045.047 3.477 3.572-6.19 6.143L8.238.192 8.434 0H6.289zm6.759 0 1.047 1.047L15.143 0h-2.094zm14.474 0 7.143 7.143-2.428 2.523-3.43-3.523-4.855 4.572 8.238 8.142L43.857 7.143 36.686 0h-2.008l7.226 7.191-9.666 9.713L26 10.762l2.762-2.57 3.476 3.617 4.477-4.618L29.572.047 29.62 0h-2.096zm14.288 0 4.808 4.809 4.572-4.57L50.953 0h-2.047l.285.285-2.572 2.572L43.762 0H41.81zm14.068 0-17.64 17.904 4.57 4.62L65 .333 64.697 0H62.79l.258.285-20.19 20.192-2.572-2.573L57.857 0H55.88zm14.549 0-3.62 3.62 8.145 8.142L86.666.047 86.619 0h-1.951l.047.047L75 9.762l-6.143-6.143 2.57-2.572L75 4.619 79.62 0h-2.048l-2.619 2.62L72.334 0h-1.906zm21.43 0-3.62 3.62 4.57 4.571L100 .998V0h-.953l-6.19 6.191-2.572-2.572L93.905 0h-2.048zm-88 2.191L0 6.091v2.04l3.762-3.846 2.666 3.049-6.381 6.19-.047-.05v2.098l8.38-8.095L3.858 2.19zM100 6.091l-4.572 4.624L100 15.572v-2.097l-2.572-2.713L100 8.13V6.09zm-35.762.1-4.572 4.57 4.572 4.573 4.57-4.572-4.57-4.57zm21.428 0L74.953 16.904l-3.572-3.57-4.572 4.57 8.144 8.143 15.285-15.285-4.572-4.57zm-21.428 2 2.57 2.57-2.57 2.573-2.572-2.572 2.572-2.57zm21.477 0 2.57 2.57L75 24.048l-6.143-6.143 2.57-2.57L75 18.904 85.715 8.191zM10.666 10 0 20.668v2L10.666 12l2.572 2.572L0 27.811v2l15.238-15.239L10.666 10zm46.43 3.334L42.809 27.619l-7.381-7.38-8.143 8.142 4.572 4.572 3.57-3.572 2.811 2.81-6.142 6.143L21.38 27.619l-4.572 4.572 15.287 15.286 25-25L67.809 33.19l4.572-4.572-15.285-15.285zm35.713.238-4.57 4.57 11.714 11.715.047-.046v-1.954l-9.715-9.714 2.572-2.57L100 22.714v-2.047l-.047.047-7.144-7.143zm-71.143.094-12 11.38 4.572 4.669 7.334-7.238 3.381 3.57 4.62-4.62-7.907-7.761zm35.43 1.62L70.38 28.571l-2.572 2.57-10.713-10.714-25 25-13.287-13.285 2.572-2.57 10.715 10.712 8.142-8.142-4.81-4.809-3.57 3.57-2.573-2.57 6.143-6.143 7.38 7.381 14.288-14.287zm-35.477.429 5.904 5.762L25 24l-3.38-3.523-7.335 7.285-2.57-2.62 9.904-9.427zm64.047 4.762-4.57 4.57L96.38 40.334l3.619-3.62V34.81L85.666 20.477zm.049 2L99 35.762l-2.572 2.572-13.285-13.287 2.572-2.57zm-6.953 4.904L67.809 38.334l-3.57-3.572-4.573 4.572 8.143 8.143 15.525-15.524-4.572-4.572zm-71.666.238-4.573 4.572L17.81 47.477l4.572-4.573L7.096 27.62zm50 0L45.38 39.334l4.572 4.57 11.713-11.713-4.57-4.572zm21.713 1.762 2.572 2.572-13.524 13.524-6.142-6.143 2.57-2.572 3.572 3.572L78.81 29.381zm-71.713.238L20.38 42.904l-2.572 2.573L4.523 32.19l2.573-2.572zm50 0 2.57 2.572-9.713 9.713-2.572-2.57 9.715-9.715zm28.57 5.143-4.57 4.572 11.713 11.713 4.572-4.57-11.715-11.715zM0 34.809v1.906l.953-.953L0 34.809zm85.715 1.953 9.713 9.715-2.57 2.57-9.715-9.713 2.572-2.572zm-82.192 1.81L0 41.877v2.08l2.523 2.424L0 48.973v2.12l7.096 7.098 8.142-8.144L3.523 38.572zm0 1.951L13.238 50l-6.142 6.143L.953 50l3.57-3.715L1 42.857l2.523-2.334zM100 41.877l-1.096 1.027L100 43.957v-2.08zm-57.191.027-4.57 4.573 4.57 4.57 4.572-4.57-4.572-4.573zm14.287 0L45.38 53.62l4.572 4.572 7.143-7.144 10.713 10.715 4.572-4.57-15.285-15.288zm21.476 0-8.144 8.143L75 54.619l3.572-3.572 7.143 7.144 4.57-4.572-11.713-11.715zm-35.763 2 2.572 2.573-2.572 2.57-2.57-2.57 2.57-2.573zm14.287 0L70.38 57.191l-2.572 2.57-10.713-10.714-7.143 7.144-2.572-2.572 9.715-9.715zm21.476 0 9.713 9.715-2.57 2.572-7.143-7.144L75 52.619l-2.572-2.572 6.144-6.143zm-53.619 1.573L9.666 60.762l4.572 4.572 15.285-15.287-4.57-4.57zm0 2 2.57 2.57-13.285 13.287-2.572-2.572 13.287-13.285zM100 48.973l-1.047 1.074L100 51.094v-2.121zm-64.334.074-4.57 4.572 18.857 18.858 11.713-11.715-4.57-4.57-7.143 7.142-14.287-14.287zm0 2 14.287 14.287 7.143-7.143 2.57 2.57-9.713 9.716-16.857-16.858 2.57-2.572zm60.953 1.81-4.572 4.57L100 65.382v-2.049l-5.904-5.904 2.57-2.57L100 58.19v-1.953l-3.38-3.38zm-68.096 3.334L9.666 75.047l4.572 4.572 7.143-7.142 7.142 7.142 4.573-4.572-7.143-7.143 2.57-2.57 7.143 7.143 4.572-4.573-11.715-11.713zm50 0-8.142 8.143 8.142 8.143 4.573-4.573-3.573-3.57 3.573-3.572-4.573-4.57zM0 56.238v2l9.666 9.666-2.57 2.573L0 63.38v2l7.096 7.096 4.57-4.573L0 56.238zm28.523 1.905 9.715 9.714-2.572 2.57-7.143-7.142-4.57 4.572L31.096 75l-2.573 2.572-7.142-7.144-7.143 7.144L11.666 75l16.857-16.857zm50 .048 2.573 2.57-3.573 3.573 3.573 3.57-2.573 2.573-6.142-6.143 6.142-6.143zm10.715 1.57-4.572 4.573 3.572 3.57-6.904 6.905 4.57 4.572 11.477-11.477-8.143-8.142zm.047 2 6.143 6.143-9.475 9.477-2.572-2.572 6.904-6.905-3.57-3.57 2.57-2.572zm-25.047 1.573-8.142 8.143 4.57 4.57 8.143-8.143-4.57-4.57zm0 2 2.57 2.57-6.142 6.143-2.57-2.57 6.142-6.143zm-21.43 5.143L28.524 84.762l-7.142-7.143-4.572 4.57 11.714 11.715 18.858-18.857-4.572-4.57zm28.573 0-4.572 4.57 11.714 11.715 4.573-4.573L71.38 70.477zm28.572 0L88.238 82.189l4.57 4.43 7.145-7 .047.047v-2.094l-7.143 7.047-2.572-2.476L100 72.428v-1.905l-.047-.046zM0 70.523v1.952l16.809 16.81-2.57 2.572-3.573-3.572-7.143 7.143L0 91.904v2.05l3.523 3.523 7.143-7.143 3.572 3.57 4.57-4.57L0 70.524zm42.809 1.954 2.572 2.57-16.858 16.857-9.714-9.715 2.572-2.57 7.142 7.143L42.81 72.477zm28.572 0 9.715 9.712-2.573 2.573-9.714-9.715 2.572-2.57zm-17.858 1.57-4.57 4.572 4.57 4.57 4.573-4.57-4.573-4.572zm0 2 2.573 2.572-2.573 2.57-2.57-2.57 2.57-2.572zM0 77.619v2.047l6.096 6.096-2.573 2.666L0 84.81v1.953l3.523 3.664 4.573-4.713L0 77.619zm64.238 0L53.523 88.334l-7.142-7.145-4.572 4.573 11.714 11.715L68.81 82.189l-4.57-4.57zm0 2 2.57 2.57-13.285 13.288-9.714-9.715 2.572-2.573 7.142 7.145 10.715-10.715zm7.143 5.143-8.143 8.142 4.57 4.573 3.573-3.573 3.572 3.573 4.57-4.573-8.142-8.142zm14.285 0-4.57 4.572L89 97.238l4.572-4.572-7.906-7.904zm14.287 0-4.572 4.572 4.619 4.62v-2.097l-2.572-2.572 2.572-2.57V84.81l-.047-.05zm-28.525 1.953 6.144 6.142L75 95.427l-3.572-3.57-3.57 3.57-2.573-2.57 6.143-6.142zm14.287.047 5.904 5.904-2.572 2.572-5.904-5.904 2.572-2.572zm-46.477 1.572-4.572 4.57L41.762 100h2l-7.096-7.096 2.572-2.57L48.906 100h2L39.238 88.334zm-18.095 3.57-7.096 7.143-3.332-3.332L6.289 100h2.143l2.33-2.285L13.049 100h2.094l6.095-6.096L24 96.524 20.71 100h1.999L26 96.428l-4.857-4.524zm75.476 3.334L91.857 100h2.047l2.762-2.762 2.572 2.57-.191.192H100v-1.38l-3.38-3.382zm-36.142.096L55.879 100h1.978l2.57-2.62L62.79 100h1.908l-4.22-4.666zm-28.381.094L27.523 100h2.096l2.524-2.523L34.678 100h2.008l-4.59-4.572zm50 .049L77.572 100h2.047l2.524-2.523L84.668 100h1.951l-4.523-4.523zM0 98.617V100h1l.191-.191L0 98.617zm71.38.43-.952.953h1.906l-.953-.953z'  strokeWidth='1' stroke='none' fill={textureColor} />
      </Pattern>
      <Pattern id='texture-brick' patternUnits='userSpaceOnUse' width='60' height='30' patternTransform='scale(.1) rotate(0)'>
        <Path d='M1-6.5v13h28v-13H1zm15 15v13h28v-13H16zm-15 15v13h28v-13H1z'  strokeWidth='2.1' stroke={textureColor} fill="none"/>
        <Path d='M31-6.5v13h28v-13H31zm-45 15v13h28v-13h-28zm60 0v13h28v-13H46zm-15 15v13h28v-13H31z'  strokeWidth='2.1' stroke={textureColor} fill="none" />
      </Pattern>
      <Pattern id='texture-hexagon' patternUnits='userSpaceOnUse' width='29' height='50.115' patternTransform='scale(.225) rotate(30) translate(14.5)'>
        <Path d='M14.5 6.628L8.886 3.372v-6.515L14.502-6.4l5.612 3.257-.001 6.514zm0 50.06l-5.613-3.256v-6.515l5.614-3.258 5.612 3.257-.001 6.515zm14.497-25.117l-5.612-3.257v-6.515L29 18.541l5.612 3.257-.001 6.515zm-29 0l-5.612-3.257v-6.515L0 18.541l5.612 3.257v6.515zM14.5 11.82L4.36 5.967l.002-11.706 10.14-5.855L24.638-5.74l-.001 11.707zm0 50.06L4.36 56.028l.002-11.706 10.14-5.855 10.137 5.852-.001 11.707zm14.498-25.118L18.858 30.91l.002-11.707L29 13.349l10.137 5.853-.001 11.706zm-29 0l-10.139-5.852.002-11.707L0 13.349l10.138 5.853-.002 11.706zm14.501-19.905L0 8.488.002-8.257l14.5-8.374L29-8.26l-.002 16.745zm0 50.06L0 58.548l.002-16.745 14.5-8.373L29 41.8l-.002 16.744zM28.996 41.8l-14.498-8.37.002-16.744L29 8.312l14.498 8.37-.002 16.745zm-29 0l-14.498-8.37.002-16.744L0 8.312l14.498 8.37-.002 16.745z'  strokeLinecap='square' strokeWidth='1.5' stroke={textureColor} fill='none'/>
      </Pattern>
      <Pattern id='texture-stripe' patternUnits='userSpaceOnUse' width='20' height='20' patternTransform='scale(.08) rotate(75)'>
        <Path d='M0 10h20z' strokeWidth='4' stroke={textureColor} fill='none'/>
      </Pattern>
    </Defs>
  )
  if (Platform.OS === 'ios') {
    defs = (
      <Defs>
        <Pattern id='texture-scale' patternUnits='userSpaceOnUse' width='4' height='4' viewBox="0 0 20 20">
          <Path d='M-10-10A10 10 0 00-20 0a10 10 0 0010 10A10 10 0 010 0a10 10 0 00-10-10zM10-10A10 10 0 000 0a10 10 0 0110 10A10 10 0 0120 0a10 10 0 00-10-10zM30-10A10 10 0 0020 0a10 10 0 0110 10A10 10 0 0140 0a10 10 0 00-10-10zM-10 10a10 10 0 00-10 10 10 10 0 0010 10A10 10 0 010 20a10 10 0 00-10-10zM10 10A10 10 0 000 20a10 10 0 0110 10 10 10 0 0110-10 10 10 0 00-10-10zM30 10a10 10 0 00-10 10 10 10 0 0110 10 10 10 0 0110-10 10 10 0 00-10-10z'  strokeWidth='1.5' stroke={textureColor} fill='none'/>
        </Pattern>
        <Pattern id='texture-chevron' patternUnits='userSpaceOnUse' width='2.4' height='2.4' viewBox="0 0 20 20">
          <Path d='M-5 5L5.1 15 15 5l10 10'  strokeLinecap='square' strokeWidth='2' stroke={textureColor} fill='none'/>
        </Pattern>
        <Pattern id='texture-geometric' patternUnits='userSpaceOnUse' width='20' height='20' viewBox="0 0 100 100">
          <Path d='M0 0v1l1-1H0zm6.29 0-.147.143 11.81 11.619 8.19-8.143L22.666.047 22.709 0h-1.998l-.045.047 3.477 3.572-6.19 6.143L8.238.192 8.434 0H6.289zm6.759 0 1.047 1.047L15.143 0h-2.094zm14.474 0 7.143 7.143-2.428 2.523-3.43-3.523-4.855 4.572 8.238 8.142L43.857 7.143 36.686 0h-2.008l7.226 7.191-9.666 9.713L26 10.762l2.762-2.57 3.476 3.617 4.477-4.618L29.572.047 29.62 0h-2.096zm14.288 0 4.808 4.809 4.572-4.57L50.953 0h-2.047l.285.285-2.572 2.572L43.762 0H41.81zm14.068 0-17.64 17.904 4.57 4.62L65 .333 64.697 0H62.79l.258.285-20.19 20.192-2.572-2.573L57.857 0H55.88zm14.549 0-3.62 3.62 8.145 8.142L86.666.047 86.619 0h-1.951l.047.047L75 9.762l-6.143-6.143 2.57-2.572L75 4.619 79.62 0h-2.048l-2.619 2.62L72.334 0h-1.906zm21.43 0-3.62 3.62 4.57 4.571L100 .998V0h-.953l-6.19 6.191-2.572-2.572L93.905 0h-2.048zm-88 2.191L0 6.091v2.04l3.762-3.846 2.666 3.049-6.381 6.19-.047-.05v2.098l8.38-8.095L3.858 2.19zM100 6.091l-4.572 4.624L100 15.572v-2.097l-2.572-2.713L100 8.13V6.09zm-35.762.1-4.572 4.57 4.572 4.573 4.57-4.572-4.57-4.57zm21.428 0L74.953 16.904l-3.572-3.57-4.572 4.57 8.144 8.143 15.285-15.285-4.572-4.57zm-21.428 2 2.57 2.57-2.57 2.573-2.572-2.572 2.572-2.57zm21.477 0 2.57 2.57L75 24.048l-6.143-6.143 2.57-2.57L75 18.904 85.715 8.191zM10.666 10 0 20.668v2L10.666 12l2.572 2.572L0 27.811v2l15.238-15.239L10.666 10zm46.43 3.334L42.809 27.619l-7.381-7.38-8.143 8.142 4.572 4.572 3.57-3.572 2.811 2.81-6.142 6.143L21.38 27.619l-4.572 4.572 15.287 15.286 25-25L67.809 33.19l4.572-4.572-15.285-15.285zm35.713.238-4.57 4.57 11.714 11.715.047-.046v-1.954l-9.715-9.714 2.572-2.57L100 22.714v-2.047l-.047.047-7.144-7.143zm-71.143.094-12 11.38 4.572 4.669 7.334-7.238 3.381 3.57 4.62-4.62-7.907-7.761zm35.43 1.62L70.38 28.571l-2.572 2.57-10.713-10.714-25 25-13.287-13.285 2.572-2.57 10.715 10.712 8.142-8.142-4.81-4.809-3.57 3.57-2.573-2.57 6.143-6.143 7.38 7.381 14.288-14.287zm-35.477.429 5.904 5.762L25 24l-3.38-3.523-7.335 7.285-2.57-2.62 9.904-9.427zm64.047 4.762-4.57 4.57L96.38 40.334l3.619-3.62V34.81L85.666 20.477zm.049 2L99 35.762l-2.572 2.572-13.285-13.287 2.572-2.57zm-6.953 4.904L67.809 38.334l-3.57-3.572-4.573 4.572 8.143 8.143 15.525-15.524-4.572-4.572zm-71.666.238-4.573 4.572L17.81 47.477l4.572-4.573L7.096 27.62zm50 0L45.38 39.334l4.572 4.57 11.713-11.713-4.57-4.572zm21.713 1.762 2.572 2.572-13.524 13.524-6.142-6.143 2.57-2.572 3.572 3.572L78.81 29.381zm-71.713.238L20.38 42.904l-2.572 2.573L4.523 32.19l2.573-2.572zm50 0 2.57 2.572-9.713 9.713-2.572-2.57 9.715-9.715zm28.57 5.143-4.57 4.572 11.713 11.713 4.572-4.57-11.715-11.715zM0 34.809v1.906l.953-.953L0 34.809zm85.715 1.953 9.713 9.715-2.57 2.57-9.715-9.713 2.572-2.572zm-82.192 1.81L0 41.877v2.08l2.523 2.424L0 48.973v2.12l7.096 7.098 8.142-8.144L3.523 38.572zm0 1.951L13.238 50l-6.142 6.143L.953 50l3.57-3.715L1 42.857l2.523-2.334zM100 41.877l-1.096 1.027L100 43.957v-2.08zm-57.191.027-4.57 4.573 4.57 4.57 4.572-4.57-4.572-4.573zm14.287 0L45.38 53.62l4.572 4.572 7.143-7.144 10.713 10.715 4.572-4.57-15.285-15.288zm21.476 0-8.144 8.143L75 54.619l3.572-3.572 7.143 7.144 4.57-4.572-11.713-11.715zm-35.763 2 2.572 2.573-2.572 2.57-2.57-2.57 2.57-2.573zm14.287 0L70.38 57.191l-2.572 2.57-10.713-10.714-7.143 7.144-2.572-2.572 9.715-9.715zm21.476 0 9.713 9.715-2.57 2.572-7.143-7.144L75 52.619l-2.572-2.572 6.144-6.143zm-53.619 1.573L9.666 60.762l4.572 4.572 15.285-15.287-4.57-4.57zm0 2 2.57 2.57-13.285 13.287-2.572-2.572 13.287-13.285zM100 48.973l-1.047 1.074L100 51.094v-2.121zm-64.334.074-4.57 4.572 18.857 18.858 11.713-11.715-4.57-4.57-7.143 7.142-14.287-14.287zm0 2 14.287 14.287 7.143-7.143 2.57 2.57-9.713 9.716-16.857-16.858 2.57-2.572zm60.953 1.81-4.572 4.57L100 65.382v-2.049l-5.904-5.904 2.57-2.57L100 58.19v-1.953l-3.38-3.38zm-68.096 3.334L9.666 75.047l4.572 4.572 7.143-7.142 7.142 7.142 4.573-4.572-7.143-7.143 2.57-2.57 7.143 7.143 4.572-4.573-11.715-11.713zm50 0-8.142 8.143 8.142 8.143 4.573-4.573-3.573-3.57 3.573-3.572-4.573-4.57zM0 56.238v2l9.666 9.666-2.57 2.573L0 63.38v2l7.096 7.096 4.57-4.573L0 56.238zm28.523 1.905 9.715 9.714-2.572 2.57-7.143-7.142-4.57 4.572L31.096 75l-2.573 2.572-7.142-7.144-7.143 7.144L11.666 75l16.857-16.857zm50 .048 2.573 2.57-3.573 3.573 3.573 3.57-2.573 2.573-6.142-6.143 6.142-6.143zm10.715 1.57-4.572 4.573 3.572 3.57-6.904 6.905 4.57 4.572 11.477-11.477-8.143-8.142zm.047 2 6.143 6.143-9.475 9.477-2.572-2.572 6.904-6.905-3.57-3.57 2.57-2.572zm-25.047 1.573-8.142 8.143 4.57 4.57 8.143-8.143-4.57-4.57zm0 2 2.57 2.57-6.142 6.143-2.57-2.57 6.142-6.143zm-21.43 5.143L28.524 84.762l-7.142-7.143-4.572 4.57 11.714 11.715 18.858-18.857-4.572-4.57zm28.573 0-4.572 4.57 11.714 11.715 4.573-4.573L71.38 70.477zm28.572 0L88.238 82.189l4.57 4.43 7.145-7 .047.047v-2.094l-7.143 7.047-2.572-2.476L100 72.428v-1.905l-.047-.046zM0 70.523v1.952l16.809 16.81-2.57 2.572-3.573-3.572-7.143 7.143L0 91.904v2.05l3.523 3.523 7.143-7.143 3.572 3.57 4.57-4.57L0 70.524zm42.809 1.954 2.572 2.57-16.858 16.857-9.714-9.715 2.572-2.57 7.142 7.143L42.81 72.477zm28.572 0 9.715 9.712-2.573 2.573-9.714-9.715 2.572-2.57zm-17.858 1.57-4.57 4.572 4.57 4.57 4.573-4.57-4.573-4.572zm0 2 2.573 2.572-2.573 2.57-2.57-2.57 2.57-2.572zM0 77.619v2.047l6.096 6.096-2.573 2.666L0 84.81v1.953l3.523 3.664 4.573-4.713L0 77.619zm64.238 0L53.523 88.334l-7.142-7.145-4.572 4.573 11.714 11.715L68.81 82.189l-4.57-4.57zm0 2 2.57 2.57-13.285 13.288-9.714-9.715 2.572-2.573 7.142 7.145 10.715-10.715zm7.143 5.143-8.143 8.142 4.57 4.573 3.573-3.573 3.572 3.573 4.57-4.573-8.142-8.142zm14.285 0-4.57 4.572L89 97.238l4.572-4.572-7.906-7.904zm14.287 0-4.572 4.572 4.619 4.62v-2.097l-2.572-2.572 2.572-2.57V84.81l-.047-.05zm-28.525 1.953 6.144 6.142L75 95.427l-3.572-3.57-3.57 3.57-2.573-2.57 6.143-6.142zm14.287.047 5.904 5.904-2.572 2.572-5.904-5.904 2.572-2.572zm-46.477 1.572-4.572 4.57L41.762 100h2l-7.096-7.096 2.572-2.57L48.906 100h2L39.238 88.334zm-18.095 3.57-7.096 7.143-3.332-3.332L6.289 100h2.143l2.33-2.285L13.049 100h2.094l6.095-6.096L24 96.524 20.71 100h1.999L26 96.428l-4.857-4.524zm75.476 3.334L91.857 100h2.047l2.762-2.762 2.572 2.57-.191.192H100v-1.38l-3.38-3.382zm-36.142.096L55.879 100h1.978l2.57-2.62L62.79 100h1.908l-4.22-4.666zm-28.381.094L27.523 100h2.096l2.524-2.523L34.678 100h2.008l-4.59-4.572zm50 .049L77.572 100h2.047l2.524-2.523L84.668 100h1.951l-4.523-4.523zM0 98.617V100h1l.191-.191L0 98.617zm71.38.43-.952.953h1.906l-.953-.953z'  strokeWidth='1' stroke='none' fill={textureColor} />
        </Pattern>
        <Pattern id='texture-brick' patternUnits='userSpaceOnUse' width='6' height='3' viewBox="0 0 60 30">
          <Path d='M1-6.5v13h28v-13H1zm15 15v13h28v-13H16zm-15 15v13h28v-13H1z'  strokeWidth='2.4' stroke={textureColor} fill="none" />
          <Path d='M31-6.5v13h28v-13H31zm-45 15v13h28v-13h-28zm60 0v13h28v-13H46zm-15 15v13h28v-13H31z'  strokeWidth='2.4' stroke={textureColor} fill="none" />
        </Pattern>
        <Pattern id='texture-hexagon' patternUnits='userSpaceOnUse' width='5.8' height='10.023' viewBox="0 0 29 50.115" x="5">
          <Path d='M14.5 6.628L8.886 3.372v-6.515L14.502-6.4l5.612 3.257-.001 6.514zm0 50.06l-5.613-3.256v-6.515l5.614-3.258 5.612 3.257-.001 6.515zm14.497-25.117l-5.612-3.257v-6.515L29 18.541l5.612 3.257-.001 6.515zm-29 0l-5.612-3.257v-6.515L0 18.541l5.612 3.257v6.515zM14.5 11.82L4.36 5.967l.002-11.706 10.14-5.855L24.638-5.74l-.001 11.707zm0 50.06L4.36 56.028l.002-11.706 10.14-5.855 10.137 5.852-.001 11.707zm14.498-25.118L18.858 30.91l.002-11.707L29 13.349l10.137 5.853-.001 11.706zm-29 0l-10.139-5.852.002-11.707L0 13.349l10.138 5.853-.002 11.706zm14.501-19.905L0 8.488.002-8.257l14.5-8.374L29-8.26l-.002 16.745zm0 50.06L0 58.548l.002-16.745 14.5-8.373L29 41.8l-.002 16.744zM28.996 41.8l-14.498-8.37.002-16.744L29 8.312l14.498 8.37-.002 16.745zm-29 0l-14.498-8.37.002-16.744L0 8.312l14.498 8.37-.002 16.745z'  strokeLinecap='square' strokeWidth='1.5' stroke={textureColor} fill='none'/>
        </Pattern>
        <Pattern id='texture-stripe' patternUnits='userSpaceOnUse' width='1.5' height='1.5' viewBox="0 0 20 20">
          <Path d='M0 0v20z' strokeWidth='8' stroke={textureColor} fill='none'/>
        </Pattern>
      </Defs>
    )
  }


  return (
    <Animated.View style={translationStyle}>
      <AnimatedSvg
        {...svgProps}
        viewBox={viewBox} version="1.1" xmlns="http://www.w3.org/2000/svg"
        {...touchEvents}
      >
        { defs }
        { props.children }
      </AnimatedSvg>
    </Animated.View>
  );
}

export default withBoardContext(HexGrid);
